import { useState } from "react";
import { Link } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Reveal } from "@/components/Reveal";
import { MailCheck } from "lucide-react";
import { Turnstile, TURNSTILE_ENABLED } from "@/components/Turnstile";

export default function ForgotPassword() {
  const { requestPasswordReset } = useCustomerAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [token, setToken] = useState("");
  const [resetSignal, setResetSignal] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_ENABLED && !token) {
      setError("Please complete the human verification.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await requestPasswordReset(email.trim(), token);
      setSent(true);
    } catch (err) {
      setError(err.message);
      setResetSignal((n) => n + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16 min-h-[70vh]" data-testid="forgot-password-page">
      <Reveal className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.28em] text-pink">Account</p>
        <h1 className="font-serif text-4xl mt-3 text-charcoal">Reset Password</h1>
        <div className="w-16 h-px bg-gold mx-auto mt-5" />
      </Reveal>

      {sent ? (
        <div className="text-center" data-testid="forgot-sent">
          <MailCheck className="w-12 h-12 text-gold mx-auto" />
          <p className="text-gray-600 mt-5 leading-relaxed">
            If an account exists for <strong>{email}</strong>, we've sent a password reset link. Please check your inbox.
          </p>
          <Link to="/login" className="inline-block mt-8 bg-pink text-white px-8 py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors">Back to Sign In</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-gray-500 text-center">Enter your email and we'll send you a reset link.</p>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address" data-testid="forgot-email"
            className="w-full border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-pink" />
          <Turnstile onVerify={setToken} action="password-reset" resetSignal={resetSignal} />
          {error && <p className="text-sm text-pink">{error}</p>}
          <button type="submit" disabled={loading} data-testid="forgot-submit"
            className="w-full bg-pink text-white py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors disabled:opacity-60">
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
          <p className="text-center text-sm text-gray-500 mt-2">
            <Link to="/login" className="text-pink hover:underline">Back to sign in</Link>
          </p>
        </form>
      )}
    </div>
  );
}
